import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import {
  type AssetsFehlenDetails,
  type FunnelPageRow,
  type FunnelPageType,
  type FunnelPatchFields,
  patchFunnelContent,
} from './api';
import { ConfirmDialog } from './ui';

/**
 * Reiter „HTML": das gespeicherte body_html als Codefeld (Spec § 5 „HTML-Reiter bleibt").
 *
 * Speichert ueber PATCH …/content mit NUR den geaenderten Feldern — nie ein voller PUT: der alte
 * Dialog schickte `subheadline: null, bullets: []` und ueberschrieb damit alles, was er nicht kannte
 * (Codex P1, Spec § 2). Kampagne/Preis/Countdown stehen im Reiter „Einstellungen".
 *
 * Bezugsstand (`basis`) statt Neu-Mounten (Codex 2026-10-08): nach einem Speichern setzt der Reiter
 * seinen Bezugsstand SOFORT aus der Antwort des PATCH (`stand`, bereinigt). Laedt der Dialog danach
 * die Seiten neu, uebernimmt der Reiter den Server-Stand nur, wenn er gerade nichts Ungespeichertes
 * traegt — was zwischen Speichern und Neuladen getippt wurde, bleibt stehen. Der Eltern-Dialog
 * mountet ihn deshalb nur noch je Seitentyp (key = pageType), nicht mehr je Neuladen.
 *
 * Fehlende Dateien (23.10., L9GkX8 Danke Version 83): Code aus LPM verweist auf `assets/…`, die nur ein
 * Upload zu uns holt. Der Server antwortet dann 409 `assets_fehlen`; der Reiter fragt nach und sendet erst
 * auf „Trotzdem speichern" mit `bestaetigt`. Veroeffentlichen bleibt dann gesperrt (Regel asset_unerreichbar).
 */

const MARKER_HINWEIS: Record<FunnelPageType, string> = {
  order:
    'Marker: <div data-lp-order-form></div> bzw. {orderform} (Formular), <div data-lp-countdown></div>. ' +
    'Skripte und on*-Attribute entfernt der Server.',
  upsell:
    'Marker: <div data-lp-upsell></div> bzw. {orderform} (Kauf-Widget), <div data-lp-vimeo="ID"></div>, ' +
    'data-lp-progress="Sekunden" (Ladebalken). Skripte und on*-Attribute entfernt der Server.',
  thanks:
    'Marker: <div data-lp-vimeo="ID"></div> (Video). Skripte und on*-Attribute entfernt der Server.',
};

interface Props {
  webinarId: string;
  pageType: FunnelPageType;
  page: FunnelPageRow;
  onSaved: (label: string, warnings: string[]) => void;
  onDirtyChange: (dirty: boolean) => void;
}

type Stand = { headline: string; body_html: string };
const gleich = (a: Stand, b: Stand) => a.headline === b.headline && a.body_html === b.body_html;

export default function HtmlPanel({ webinarId, pageType, page, onSaved, onDirtyChange }: Props) {
  const [basis, setBasis] = useState<Stand>(() => ({
    headline: page.headline || '',
    body_html: page.body_html || '',
  }));
  const [form, setForm] = useState<Stand>(basis);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rueckfrage, setRueckfrage] = useState<AssetsFehlenDetails | null>(null);
  // Fuer den Seiten-Effekt: der aktuelle Stand, ohne den Effekt bei jedem Tastendruck neu zu fahren.
  const aktuell = useRef({ basis, form });
  aktuell.current = { basis, form };

  // Neu geladene Zeile: nur uebernehmen, wenn nichts Ungespeichertes im Reiter steht — sonst gingen
  // Eingaben verloren, die zwischen Speichern und Neuladen getippt wurden.
  useEffect(() => {
    const vonSeite: Stand = { headline: page.headline || '', body_html: page.body_html || '' };
    const { basis: b, form: f } = aktuell.current;
    if (!gleich(f, b) || gleich(b, vonSeite)) return;
    setBasis(vonSeite);
    setForm(vonSeite);
  }, [page.headline, page.body_html]);

  const changed: FunnelPatchFields = useMemo(() => {
    const out: FunnelPatchFields = {};
    if (form.headline !== basis.headline) out.headline = form.headline;
    if (form.body_html !== basis.body_html) out.body_html = form.body_html;
    return out;
  }, [form, basis]);
  const dirty = Object.keys(changed).length > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const save = async (bestaetigt = false) => {
    if (!dirty) return;
    if (changed.headline !== undefined && !changed.headline.trim()) {
      setError('Der Seitentitel darf nicht leer sein.');
      return;
    }
    const gesendet = form;
    setBusy(true);
    setError(null);
    const res = await patchFunnelContent(webinarId, pageType, changed, 'code', bestaetigt);
    setBusy(false);
    if (!res.success && res.details?.code === 'assets_fehlen') {
      setRueckfrage(res.details as AssetsFehlenDetails);
      return;
    }
    if (!res.success || !res.data) {
      setError(res.error || 'Speichern fehlgeschlagen');
      return;
    }
    // Bezugsstand SOFORT aus der Antwort (bereinigter Stand der Zeile); ein Server ohne `stand`:
    // der gesendete Stand. Wurde waehrend des PATCH weitergetippt, bleibt das Getippte im Feld.
    const stand = res.data.stand;
    const neu: Stand = stand
      ? { headline: stand.headline || '', body_html: stand.body_html || '' }
      : gesendet;
    setBasis(neu);
    setForm((f) => (gleich(f, gesendet) ? neu : f));
    const hinweise = [...(res.data.warnings || [])];
    if (bestaetigt) {
      hinweise.push(
        'Dateien fehlen — Veröffentlichen bleibt gesperrt, bis die Seite in Schritt 1 als ZIP mit Bildern hochgeladen ist.',
      );
    }
    onSaved(`HTML gespeichert (Version ${res.data.versionId})`, hinweise);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" data-testid="html-hinweis">
        Nur Texte ändern? Dafür ist „Texte bearbeiten“ da (Knopf oben bei der Seite). Eine Seite aus LPM
        bitte in Schritt 1 als ZIP mit Bildern hochladen — hier eingefügter Code bringt keine Bilder
        mit.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField
        label="Seitentitel (Browser-Tab)"
        value={form.headline}
        onChange={(e) => setForm((prev) => ({ ...prev, headline: e.target.value }))}
        fullWidth
        size="small"
      />
      <TextField
        label="Seiten-HTML"
        value={form.body_html}
        onChange={(e) => setForm((prev) => ({ ...prev, body_html: e.target.value }))}
        fullWidth
        multiline
        minRows={14}
        maxRows={28}
        helperText={MARKER_HINWEIS[pageType]}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
      />
      <Box display="flex" alignItems="center" gap={1}>
        <Button variant="contained" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? 'Speichert …' : 'HTML speichern'}
        </Button>
        <Button onClick={() => setForm(basis)} disabled={busy || !dirty}>
          Verwerfen
        </Button>
        <Typography variant="caption" color="text.secondary">
          {dirty ? 'Ungespeicherte Änderungen' : 'Gespeichert'} · jede Speicherung legt eine Version
          an
        </Typography>
      </Box>
      <ConfirmDialog
        open={!!rueckfrage}
        title="Dateien liegen nicht bei uns"
        confirmLabel="Trotzdem speichern"
        danger
        busy={busy}
        onConfirm={() => {
          setRueckfrage(null);
          void save(true);
        }}
        onCancel={() => setRueckfrage(null)}
      >
        <Typography variant="body2" gutterBottom>
          Der Code verweist auf {rueckfrage?.anzahl} Datei(en), die nicht auf unserem Server liegen — auf
          der Seite würden diese Bilder, Schriften oder Stile fehlen:
        </Typography>
        <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5, fontFamily: 'monospace', fontSize: 12 }}>
          {rueckfrage?.verweise.map((v) => (
            <li key={v}>{v}</li>
          ))}
          {rueckfrage && rueckfrage.anzahl > rueckfrage.verweise.length && (
            <li>… und {rueckfrage.anzahl - rueckfrage.verweise.length} weitere</li>
          )}
        </Box>
        <Typography variant="body2">
          Seiten aus LPM bitte in Schritt 1 als ZIP mit Bildern hochladen; für Textänderungen „Texte
          bearbeiten“ nehmen. Wer trotzdem speichert, kann die Seite nicht veröffentlichen, bis die
          Dateien da sind.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
}
